import React, { useState } from 'react';
import { IpaType } from '../../phonemes/ipa';
import { englishAccentRules } from '../../ruleSets/accentRules';
import { findLexicalSets, lexicalSets } from '../../rules/findLexicalSets';
import { replaceLexicalTokens } from '../../rules/replaceLexicalSets';
import { RulesRunner } from '../../rules/rulesRunner';
import { speakPhonemes, stopEspeak } from '../../speak/speakPhonemes';
import { makeTransmogrification } from '../../transmogrify/makeTransmogrification';
import { ChoiceList } from '../widgets/ChoiceList';
import { getLanguageTrackers } from '../LanguageTrackers';
import { PhonemeShifter } from '../PhonemeShifter';
import { LexicalSetArea } from './LexicalSetArea';
import { ShowPhonemes, toClipboard } from './ShowPhonemes';
import { EditSpeakOptions } from '../EditSpeakOptions';
import { en_US_ipa, en_US_segments } from '../../transcribe/en_US.json';
import { worldAllophones } from '../../transmogrify/allophones';
import { RulesFromSegments } from './RulesFromSegments';
import { Rule } from '../../rules/rule';
import { ruleSetFromString } from './ruleSetFromString';
import { ruleSetToString } from './ruleSetToString';
import { RuleSet } from '../../rules/ruleSet';
import { loadEnglishDictionaries } from '../../loader/loadEnglishDictionaries';
import { loadWorldData } from '../../loader/loadWorldData';
import { IpaKeyboard } from '../widgets/IpaKeyboard';
import { PhoneLengths } from '../../rules/lengthRules';
import { speakOnCtrlS } from '../widgets/SpeakOnCtrlS';

const allSets = 'face, price, goat, choice, mouth, start, square, near, north, force, cure, letter, nurse, palm, lot, thought, cloth, trap, bath, dress, fleece, happy, kit, goose, foot, strut, comma';

// local storage key for storing the rule
const customRuleKey = 'custom-rule';

/**
 * A region for editing custom phoneme-modifying rules
 */
export function CustomRulesArea() {
  const [rule, setRule] = useState<RuleSet>(() => getInitialRule());
  const [rulesRunner, setRulesRunner] = useState<RulesRunner>(() => new RulesRunner(defaultRules));
  const [ruleText, setRuleText] = useState(() => ruleSetToString(rule));
  const [ruleName, setRuleName] = useState('');
  const [shifter] = useState(() => new PhonemeShifter())
  const [lexicalSet, setLexicalSet] = useState<SelectedLexicalSet>({ type: 'none' });
  const [languageText, setLanguageText] = useState('');
  const [withLexicalSets, setWithLexicalSets] = useState('');
  const [badLanguage, setBadLanguage] = useState('');
  const [ipaInput, setIpaInput] = useState('');
  const [badIpa, setBadIpa] = useState('');
  const [output, setOutput] = useState('');
  const [badOutput, setBadOutput] = useState('');
  const [badRule, setBadRule] = useState('');
  const [allLanguages, setAllLanguages] = useState(['downloading...']);
  const [currentLanguage, setCurrentLanguage] = useState('');
  const [init, setInit] = useState(false);
  const [insert, setInsert] = useState<[string] | undefined>();

  if (!init) {
    setInit(true);
    loadEnglishDictionaries();
    loadWorldData().then(() => {
      setAllLanguages([''].concat(getLanguageTrackers().sortedByName()));
    });
  }

  function enterLanguageText(sentence: string, runner?: RulesRunner) {
    const { ipa, afterRules, badLanguage, badIpa }
      = shifter.transformLanguage(sentence, runner ? runner : rulesRunner);
    setLanguageText(sentence);
    setBadLanguage(badLanguage ? badLanguage.join(' ') : '');
    setWithLexicalSets(toLexicalSets(sentence));
    setIpaInput(ipa);
    setBadIpa(badIpa ? badIpa.join(' ') : '');
    setOutput(afterRules);
  }

  function speak(input: string, addLengths?: PhoneLengths) {
    const { unsupported } = speakPhonemes(input, undefined, addLengths);
    setBadOutput(unsupported ? unsupported.join(' ') : '');
  }
  function speakInput() {
    speak(ipaInput);
  }
  function speakOutput() {
    speak(output, rulesRunner.getAddPhonemeLength());
  }

  function parseRule(text: string) {
    const rule = ruleSetFromString(text);
    if (typeof rule === 'string') {
      // parse failure
      setBadRule(rule);
    } else {
      const runner = new RulesRunner(rule);
      if (runner.getFailures().length > 0) {
        // compile failure
        const error = 'error: ' + JSON.stringify(runner.getFailures());
        setBadRule(error);
      } else {
        // success
        window.localStorage.setItem(customRuleKey, text);
        setRule(rule);
        setRulesRunner(runner);
        setBadRule('');
        enterLanguageText(languageText, runner);
      }
    }
  }

  function pickRuleSet(name: string) {
    setRuleName(name);
    if (name !== '') {
      const rule = name === 'sample' ? defaultRules : englishAccentRules[name];
      setRule(rule);
      setRuleText(ruleSetToString(rule));
      const runner = new RulesRunner(rule);
      setRulesRunner(runner);
    }
  }
  const allRules = ['', 'sample'].concat(Object.getOwnPropertyNames(englishAccentRules));

  function pickLanguage(language: string) {
    setCurrentLanguage(language);
    const other = getLanguageTrackers().lookupStats(language);
    const result = makeTransmogrification(en_US_ipa, en_US_segments, other.chartIpa, other.segmentList, worldAllophones);
    const rule = { rules: result.rules };
    setRule(rule);
    setRuleText(ruleSetToString(rule));
    const runner = new RulesRunner(rule);
    setRulesRunner(runner);
    setBadRule(result.unhandled.length === 0 ? '' : 'unhandled: ' + result.unhandled.join());
  }

  function onRuleTextCursor(target: any) {
    const pos = (target as unknown as Select).selectionStart;
    const result = findSet(ruleText, pos);
    setLexicalSet(result);
  }

  function setRules(rules: Rule[]) {
    setRule({ rules });
    setRuleText(ruleSetToString({ rules }));
    const runner = new RulesRunner({ rules });
    setRulesRunner(runner);
  }

  return (<>
    <div style={{ display: 'grid', gridTemplateColumns: 'auto 35%' }}>
      <div>
        English &nbsp;&nbsp;
        <button onClick={() => enterLanguageText(allSets)}>all lexical sets</button>
        <br />
        <textarea cols={80} rows={4} spellCheck={false} value={languageText}
          onChange={(foo) => enterLanguageText(foo.target.value)}
          onKeyDown={e => speakOnCtrlS(e, speakOutput)} />
        <br />{badLanguage === '' ? '' : 'unknown: ' + badLanguage}

        <br />Lexical sets<br />
        <textarea cols={80} rows={4} spellCheck={false} value={withLexicalSets} readOnly={true} />
        <br />

        <br />IPA version of input<br />
        <IpaKeyboard value={ipaInput} insert={insert} onChange={setIpaInput} onSpeak={speakInput} cols={80} rows={4} />
        <br />{badIpa === '' ? '' : 'unknown: ' + badIpa}

        <br />IPA with rules applied<br />
        <IpaKeyboard value={output} onChange={setOutput} onSpeak={speakOutput} cols={80} rows={4} />
        <br />{badOutput === '' ? '' : 'unknown: ' + badOutput}

        <br />
        <button onClick={speakInput}>speak IPA</button>
        &nbsp;&nbsp;
        <button onClick={speakOutput}>speak output</button>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        <button onClick={() => stopEspeak()}>stop</button>

        <br /><br />Custom rule: &nbsp;&nbsp;
        <button onClick={() => parseRule(ruleText)}>use rule</button> &nbsp;&nbsp;
        <ChoiceList items={allRules} selectedItem={ruleName} onSelect={pickRuleSet} />
        <br />
        or use: &nbsp;&nbsp;
        <ChoiceList items={allLanguages} selectedItem={currentLanguage} onSelect={pickLanguage} />
        <br />
        <textarea cols={80} rows={15} spellCheck={false} value={ruleText}
          onChange={(event) => setRuleText(event.target.value)} onSelect={(event) => onRuleTextCursor(event.target)} />
        <br />{badRule}
        <br />
        <RulesFromSegments allLanguages={allLanguages} setRules={setRules} />
      </div>
      <div>
        <ShowPhonemes clicked={(s) => setInsert([s])} />
        <p></p><hr /><p></p>
        {
          lexicalSet.type === 'none' ? undefined : lexicalSet.type === 'all'
            ? <LexicalSetArea phonemes={lexicalSet.phonemes} clicked={toClipboard} sayOnClick={'right'} />
            : <LexicalSetArea one={lexicalSet} clicked={toClipboard} sayOnClick={'right'} />
        }
      </div>
    </div>
    <hr />
    <EditSpeakOptions />
    <br /><br />
  </>
  );
}

const defaultRules: RuleSet = {
  preprocess: ['cleanPunctuation', 'lowerCase'],
  lexicalSets: {
    FACE: 'eɪ̯', PRICE: 'aɪ̯', GOAT: 'oʊ̯', CHOICE: 'ɔɪ̯', MOUTH: 'aʊ̯',
    START: 'ɑɹ', SQUARE: 'ɛɹ', NEAR: 'ɪɹ', NORTH: 'ɔɹ',
    FORCE: 'ɔɹ', CURE: 'ʊɹ', lettER: 'ɝ', NURSE: 'ɝ',
    PALM: 'ɑ', LOT: 'ɑ', THOUGHT: 'ɔ', CLOTH: 'ɔ', TRAP: 'æ', BATH: 'æ', DRESS: 'ɛ',
    FLEECE: 'i', happY: 'i', KIT: 'ɪ', GOOSE: 'u', FOOT: 'ʊ', STRUT: 'ə', commA: 'ə',
  },
  phonemeLengths: {
    map: {
      eɪ̯: 230, aɪ̯: 230, oʊ̯: 230, ɔɪ̯: 230, aʊ̯: 230,
      ɑɹ: 260, ɛɹ: 260, ɪɹ: 260, ɔɹ: 260, ʊɹ: 260, ɝ: 220,
      ɑ: 210, ɔ: 190, æ: 190, ɛ: 190, i: 190, ɪ: 190, u: 190, ʊ: 190, ə: 130
    }
  },
  rules: [
    { from: 'θ', to: 'f' },
    { from: 'ð', to: 'v', next: ['#word'] },
    { from: 'ð', to: 'v', previous: ['vowel'], next: ['vowel'] },
  ]
}

/** return the initial RuleSet, fetching from local storage if present */
function getInitialRule(): RuleSet {
  const value = window.localStorage.getItem(customRuleKey);
  if (value === null) {
    return defaultRules;
  }
  const rule = ruleSetFromString(value);
  if (typeof rule === 'string') {
    // error parsing what was stored so remove it
    window.localStorage.removeItem(customRuleKey);
    return defaultRules;
  }
  return rule;
}

/** take English, convert to IPA with the lexical set phonemes replaced with '(SETNAME)' */
function toLexicalSets(sentence: string): string {
  const tokenized = findLexicalSets(sentence);
  const lookup = lexicalSets.map(set => '(' + set.name + ')');
  return replaceLexicalTokens(tokenized, lookup);
}

type SelectedLexicalSet =
  | { type: 'none' }
  | { type: 'all', phonemes: IpaType[] }
  | { type: 'one', setName: string, phoneme: IpaType };

/** find which lexical set the cursor is in, if any
 * @return [setName, phoneme] */
function findSet(ruleText: string, pos: number): SelectedLexicalSet {
  const lexStart = ruleText.indexOf('lexicalSets:');
  const lexEnd = ruleText.indexOf('}', lexStart);
  if (lexStart < 0 || pos < lexStart || lexEnd < pos) {
    return { type: 'none' };  // pos is outside of 'lexicalSet: { }'
  } else if (lexStart <= pos && pos <= lexStart + 'lexicalSets: {'.length) {
    // if we're on 'lexicalSets' itself, show everything
    const phonemes = getAllSetPhonemes(ruleText);
    return { type: 'all', phonemes };
  }

  // find first name that pos is past
  let bestName = '';
  let bestStart = 0;
  let distance = Number.MAX_VALUE;
  lexicalSets.forEach(set => {
    const where = ruleText.indexOf(set.name, lexStart);
    if (where <= pos && (pos - where < distance)) {
      bestName = set.name;
      bestStart = where;
      distance = pos - where;
    }
  });

  // find current phoneme associated with that name
  let start = ruleText.indexOf("'", bestStart);
  let end = ruleText.indexOf("'", start + 1);
  const phoneme = (start === -1 || end === -1) ? '' : ruleText.substring(start + 1, end);
  return { type: 'one', setName: bestName, phoneme };
}

/** parse the rules & extract all the phonemes used by lexical sets */
function getAllSetPhonemes(ruleText: string): IpaType[] {
  const rule = ruleSetFromString(ruleText);
  if (typeof rule === 'string' || rule.lexicalSets === undefined) {
    return [];
  }
  const sets = rule.lexicalSets as Record<string, IpaType>;
  const names = Object.getOwnPropertyNames(sets);
  return names.map(name => sets[name]);
}

interface Select {
  readonly selectionStart: number;
}
